import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollapsibleSection } from "@/components/collapsible-section";
import { SimpleNoteTable } from "@/components/note-lists/simple-note-table";
import { useUpdateNoteMutation } from "@/mutations/notes";
import { useDeleteNoteWithUndo } from "@/hooks/useDeleteNoteWithUndo";
import type { Note } from "@/types/note";

interface RecentActivitySectionProps {
  notes: Note[];
  className?: string;
}

export function RecentActivitySection({
  notes,
  className,
}: RecentActivitySectionProps) {
  const updateNoteMutation = useUpdateNoteMutation();
  const { deleteNote } = useDeleteNoteWithUndo();

  if (notes.length === 0) return null;

  const togglingNotes = new Set<string>();
  if (updateNoteMutation.isPending && updateNoteMutation.variables?.noteId) {
    togglingNotes.add(updateNoteMutation.variables.noteId);
  }

  const handleToggleComplete = (noteId: string, completed: boolean) => {
    updateNoteMutation.mutate({
      noteId,
      status: completed ? "COMPLETED" : "NOT_STARTED",
    });
  };

  const handleDelete = (noteId: string) => {
    const note = notes.find((n) => n.id === noteId);
    if (note) deleteNote(note);
  };

  return (
    <CollapsibleSection
      title="Recent Activity"
      icon={<Sparkles className="h-4 md:h-5 w-4 md:w-5 text-purple-500" />}
      className={className}
    >
      <SimpleNoteTable
        notes={notes}
        onToggleComplete={handleToggleComplete}
        onDelete={handleDelete}
        isTogglingComplete={togglingNotes}
      />
      <Button variant="ghost" size="sm" asChild className="w-full mt-3">
        <Link to="/inbox">View all notes →</Link>
      </Button>
    </CollapsibleSection>
  );
}
